import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';

// Import page components
import HomePage from './features/home/pages/HomePage';
import AboutPage from './features/about/pages/AboutPage';
import ServicesPage from './features/services/pages/ServicesPage';
import Projects from './features/projects/pages/Projects';
import ContactPage from './features/contact/pages/Contact';
import QuotePage from './features/quote/pages/QuotePage';
import NotFound from './shared/components/NotFound/NotFound';
import Layout from './shared/components/Layout/Layout';
import LoadingSpinner from './shared/components/LoadingSpinner';
import ServiceDetail from './features/service_details/pages/ServiceDetail';
import PrivacyPolicy from './features/privacy/pages/PrivacyPolicy';
import BlogListPage from './features/blog/pages/BlogListPage';
import BlogDetailPage from './features/blog/pages/BlogDetailPage';
import ProjectDetailsPage from './features/projects/pages/ProjectDetailsPage';

// Shop routes are lazy-loaded: the whole catalog/cart/checkout flow is a large
// addition that most visitors to the marketing pages never reach.
const ShopCatalogPage = lazy(() => import('./features/shop/pages/ShopCatalogPage'));
const ShopCategoryPage = lazy(() => import('./features/shop/pages/ShopCategoryPage'));
const ProductDetailPage = lazy(() => import('./features/shop/pages/ProductDetailPage'));
const CartPage = lazy(() => import('./features/shop/pages/CartPage'));
const CheckoutPage = lazy(() => import('./features/shop/pages/CheckoutPage'));
const OrderConfirmationPage = lazy(
  () => import('./features/shop/pages/OrderConfirmationPage')
);

const ShopFallback: React.FC = () => (
  <LoadingSpinner type="clip" size={50} text="Chargement…" overlay={false} />
);

const AppRoutes: React.FC = () => {
  return (
    <Routes>

      {/* Public Routes */}
      <Route path="/" element={
        <Layout>
          <HomePage />
        </Layout>
      } />
      <Route path="/about" element={
        <Layout>
          <AboutPage />
        </Layout>
      } />
      <Route path="/services" element={
        <Layout>
          <ServicesPage />
        </Layout>
      } />
      <Route path="/services/:serviceId" element={
        <Layout>
          <ServiceDetail />
        </Layout>
      } />
      <Route path="/projects" element={
        <Layout>
          <Projects />
        </Layout>
      } />
      <Route path="/projects/:id" element={
        <Layout>
          <ProjectDetailsPage />
        </Layout>
      } />


      {/*} />*/}
      <Route path="/contact" element={
        <Layout>
          <ContactPage />
        </Layout>
      } />

      <Route path="/quote" element={
        <Layout>
          <QuotePage />
        </Layout>
      } />

      <Route path="/privacy-policy" element={
        <Layout>
          <PrivacyPolicy />
        </Layout>
      } />

      <Route path="/blog" element={
        <Layout>
          <BlogListPage />
        </Layout>
      } />

      <Route path="/blog/:slug" element={
        <Layout>
          <BlogDetailPage />
        </Layout>
      } />

      {/* Shop. Static segments must precede the /boutique/:slug catch-all. */}
      <Route path="/boutique" element={
        <Layout>
          <Suspense fallback={<ShopFallback />}>
            <ShopCatalogPage />
          </Suspense>
        </Layout>
      } />

      <Route path="/boutique/panier" element={
        <Layout>
          <Suspense fallback={<ShopFallback />}>
            <CartPage />
          </Suspense>
        </Layout>
      } />

      <Route path="/boutique/commande" element={
        <Layout>
          <Suspense fallback={<ShopFallback />}>
            <CheckoutPage />
          </Suspense>
        </Layout>
      } />

      <Route path="/boutique/commande/confirmation" element={
        <Layout>
          <Suspense fallback={<ShopFallback />}>
            <OrderConfirmationPage />
          </Suspense>
        </Layout>
      } />

      <Route path="/boutique/categorie/:slug" element={
        <Layout>
          <Suspense fallback={<ShopFallback />}>
            <ShopCategoryPage />
          </Suspense>
        </Layout>
      } />

      <Route path="/boutique/:slug" element={
        <Layout>
          <Suspense fallback={<ShopFallback />}>
            <ProductDetailPage />
          </Suspense>
        </Layout>
      } />

      {/* 404 Not Found - This must be the last route */}
      <Route path="*" element={
        <Layout>
          <NotFound />
        </Layout>
      } />
    </Routes>
  );
};

export default AppRoutes;
